import React, { useState, useEffect, useContext, useRef } from 'react';
import { motion } from 'framer-motion';
import { AIContext, aiSizeOptions } from 'contexts/AIContext';
import ImageChatHeader from './ImageChatHeader';
import ImageChatMessages from './ImageChatMessages';
import ImageChatInput from './ImageChatInput';
import ImageSelector from './ImageSelector';
import { getFileTypeImageVideo } from 'utils/fileType';
import { getImageDimensions } from 'utils/getImageWidthHeight';

// Simple UUID alternative
const generateId = () => {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Helper function to find the closest aspect ratio from available options
const findClosestAspectRatio = (imageWidth, imageHeight, availableRatios = aiSizeOptions) => {
    if (!imageWidth || !imageHeight || availableRatios.length === 0) {
        return "1:1"; // Default fallback
    }

    const imageRatio = imageWidth / imageHeight;
    let closestRatio = availableRatios[0].id;
    let minDifference = Infinity;

    availableRatios.forEach(option => {
        const [w, h] = option.aspect_ratio.split(':').map(Number);
        const optionRatio = w / h;
        const difference = Math.abs(imageRatio - optionRatio);
        
        if (difference < minDifference) {
            minDifference = difference;
            closestRatio = option.id;
        }
    });

    return closestRatio;
};

const ImageChat = ({ 
    className = "",
    initialImages = [],
    title = "Image Chat",
    placeholder = "Describe how you want to modify these images...",
    maxImages = 16,
    onClose = undefined,
    aspectRatio: initialAspectRatio = "1:1",
    showHeader = true,
    isSelectable = false,
    onImageSelect = undefined,
    onImageUnselect = undefined,
    externalSelectedImages = []
}) => {
    const { getImages, getDriverModel } = useContext(AIContext);
    const [selectedImages, setSelectedImages] = useState([]);
    const [internalSelectedImages, setInternalSelectedImages] = useState([]);
    const [messages, setMessages] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showImageSelector, setShowImageSelector] = useState(false);
    const [aspectRatio, setAspectRatio] = useState(initialAspectRatio);
    const messagesEndRef = useRef(null);
    const scrollContainerRef = useRef(null);

    // Initialize with preloaded images and detect aspect ratio
    useEffect(() => {
        if (initialImages?.length > 0) {
            const processInitialImages = async () => {
                const formattedImages = initialImages.map(url => ({
                    id: generateId(),
                    url: url,
                    type: getFileTypeImageVideo(url)
                }));
                
                const imagesToSet = formattedImages.slice(0, maxImages);
                setSelectedImages(imagesToSet);

                // Auto-detect aspect ratio from the first image if initialAspectRatio is default or not properly set
                if (initialAspectRatio === "1:1" && imagesToSet.length > 0 && imagesToSet[0].type === 'image') {
                    try {
                        const { width, height } = await getImageDimensions(imagesToSet[0].url);
                        if (width && height) {
                            const detectedAspectRatio = findClosestAspectRatio(width, height);
                            setAspectRatio(detectedAspectRatio);
                        }
                    } catch (error) {
                        console.warn('Failed to detect aspect ratio from initial image:', error);
                        // Keep the default aspect ratio
                    }
                }
            };

            processInitialImages();
        }
    }, [initialImages, maxImages, initialAspectRatio]);

    // Auto scroll to bottom with improved implementation
    useEffect(() => {
        // Use requestAnimationFrame to ensure DOM has updated
        const scrollToBottom = () => {
            requestAnimationFrame(() => {
                if (messagesEndRef.current) {
                    messagesEndRef.current.scrollIntoView({ 
                        behavior: 'smooth',
                        block: 'end',
                        inline: 'nearest'
                    });
                }
                // Also ensure the container is scrolled to bottom
                if (scrollContainerRef.current) {
                    scrollContainerRef.current.scrollTop = scrollContainerRef.current.scrollHeight;
                }
            });
        };
        
        // Add a small delay to ensure images have loaded
        const timeoutId = setTimeout(scrollToBottom, 100);
        
        return () => clearTimeout(timeoutId);
    }, [messages, isGenerating]);

    const handleAddImages = (images) => {
        const newImages = images.map(img => ({
            id: generateId(),
            url: img.originalSrc || img.url,
            type: getFileTypeImageVideo(img.originalSrc || img.url),
            asset: img.asset
        }));
        
        const combined = [...selectedImages, ...newImages];
        setSelectedImages(combined.slice(0, maxImages));
    };

    const handleRemoveImage = (imageId) => {
        setSelectedImages(prev => prev.filter(img => img.id !== imageId));
    };

    const handleAspectRatioChange = (newAspectRatio) => {
        setAspectRatio(newAspectRatio);
    };

    const handleSendMessage = async (messageText) => {
        if (!messageText.trim() || selectedImages.length === 0) return;

        // Add user message with input images saved
        const userMessage = {
            id: generateId(),
            type: 'user',
            text: messageText,
            images: [...selectedImages],
            inputImages: [...selectedImages], // Save the input images for restoration
            timestamp: new Date()
        };
        
        setMessages(prev => [...prev, userMessage]);
        setIsGenerating(true);

        try {
            // Generate images using AI with selected aspect ratio
            const imageUrls = selectedImages.map(img => img.url);
            const { model } = getDriverModel("image");
            const result = await getImages(
                `${messageText}`, 
                aspectRatio, 
                undefined, 
                false, 
                imageUrls,
                true,
                model?.isJob || false
            );
            
            // Add AI response message with input images saved
            const aiMessage = {
                id: generateId(),
                type: 'ai',
                text: messageText,
                originalImages: [...selectedImages],
                inputImages: [...selectedImages], // Save the input images for restoration
                generatedImages: result.urls.map(url => ({
                    id: generateId(),
                    url: url,
                    type: 'image'
                })),
                timestamp: new Date()
            };
            
            setMessages(prev => [...prev, aiMessage]);
            
            // Update selected images to the generated ones
            setSelectedImages(aiMessage.generatedImages);
            
        } catch (error) {
            console.error('Error generating images:', error);
            // Add error message
            const errorMessage = {
                id: generateId(),
                type: 'error',
                text: 'Failed to generate images. Please try again.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleDeleteMessage = (messageId) => {
        const messageIndex = messages.findIndex(msg => msg.id === messageId);
        if (messageIndex === -1) return;
        
        // Get the message to restore state from
        const messageToRestore = messages[messageIndex];
        
        // Delete this message and all following ones
        const updatedMessages = messages.slice(0, messageIndex);
        setMessages(updatedMessages);
        
        // Restore the selected images to the state they were in at this message
        if (messageToRestore.inputImages) {
            setSelectedImages(messageToRestore.inputImages);
        } else if (messageToRestore.images) {
            // Fallback for user messages
            setSelectedImages(messageToRestore.images);
        }
    };

    const handleSelectImages = (selectedAssets) => {
        console.log('selectedAssets', selectedAssets);
        const formattedImages = selectedAssets.map(asset => ({
            originalSrc: asset.originalSrc,
            asset: asset.asset
        }));
        handleAddImages(formattedImages);
        setShowImageSelector(false);
    };

    const handleImageSelect = (originalSrc, asset) => {
        const imageData = {
            originalSrc,
            asset,
            url: originalSrc
        };
        
        if (onImageSelect) {
            onImageSelect(originalSrc, asset);
        } else {
            setInternalSelectedImages(prev => [...prev, imageData]);
        }
    };

    const handleImageUnselect = (originalSrc, asset) => {
        if (onImageUnselect) {
            onImageUnselect(originalSrc, asset);
        } else {
            setInternalSelectedImages(prev => prev.filter(img => img.originalSrc !== originalSrc));
        }
    };

    const actualSelectedImages = onImageSelect ? externalSelectedImages : internalSelectedImages;

    return (
        <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            className={`flex flex-col h-full ${className} relative overflow-hidden`}
        >
            {/* Background Pattern */}
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 pointer-events-none"></div>
            <div className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03] pointer-events-none" style={{
                backgroundImage: `radial-gradient(circle at 1px 1px, rgb(0 0 0 / 0.15) 1px, transparent 0)`,
                backgroundSize: '40px 40px'
            }}></div>

            {/* Header */}
            {showHeader && (
                <div className="relative z-10">
                    <ImageChatHeader 
                        title={title}
                        onClose={onClose}
                        selectedImagesCount={selectedImages.length}
                        maxImages={maxImages}
                    />
                </div>
            )}

            {/* Messages Area */}
            <div className="relative flex-1 overflow-hidden">
                <ImageChatMessages 
                    ref={scrollContainerRef}
                    messages={messages}
                    isGenerating={isGenerating}
                    aspectRatio={aspectRatio}
                    onDeleteMessage={handleDeleteMessage}
                    isSelectable={isSelectable}
                    handleOnSelect={handleImageSelect}
                    handleOnUnselect={handleImageUnselect}
                    selectedImages={actualSelectedImages}
                />
                
                <div ref={messagesEndRef} />
            </div>
            
            {/* Input Area */}
            <div className="relative z-10">
                <ImageChatInput
                    selectedImages={selectedImages}
                    onRemoveImage={handleRemoveImage}
                    onSendMessage={handleSendMessage}
                    onOpenImageSelector={() => setShowImageSelector(true)}
                    placeholder={placeholder}
                    isGenerating={isGenerating}
                    maxImages={maxImages}
                    aspectRatio={aspectRatio}
                    onAspectRatioChange={handleAspectRatioChange}
                />
            </div>

            {/* Image Selector Modal */}
            <ImageSelector
                isOpen={showImageSelector}
                onClose={() => setShowImageSelector(false)}
                onSelect={(selectedAssets) => {
                    console.log('selectedAssets', selectedAssets);
                    handleSelectImages(selectedAssets);
                }}
                maxImages={maxImages - selectedImages.length}
                allowedTypes={["image"]}
            />
        </motion.div>
    );
};

export default ImageChat; 